import { HiCloudUpload, HiDocumentText, HiTrash } from "react-icons/hi";
import "./style.scss";
import { useEffect, useState } from "react";
import Loading from "../../ui/loading/Loading";
import ImageModal from "../../../../modules/renters/components/modals/ImageModel";
import PopUpDelete from "../../layout/partials/PopupDelete";
import {
  getFileExtension,
  isImageLikeFile,
  isPdfFile,
} from "../../../helpers/file-upload";

type UploadImageFieldProps = {
  className?: string;
  src: string;
  onNewFile?: (imagePath: File) => void;
  onRemoveClick?: () => void;
  disableTrashIcon?: boolean;
  isReadOnly?: boolean;
};

const ASSETS_URL = import.meta.env.VITE_ASSETS_URL;

const buildAssetURL = (key: string) => `${ASSETS_URL}/${key}`;

export function UploadImageField(props: UploadImageFieldProps) {
  const [preview, setPreview] = useState<string>();
  const [previewName, setPreviewName] = useState("");
  const [previewType, setPreviewType] = useState<"image" | "pdf" | "file">(
    "image"
  );
  const [showModal, setShowModal] = useState(false);
  const [loadingImage, setLoadingImage] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState(false);
  const { isReadOnly = false } = props;

  const isImagePreview = previewType === "image";
  const isPdfPreview = previewType === "pdf";

  function handleFileInput(e) {
    e.preventDefault();
    e.stopPropagation();
    if (isReadOnly) return;

    if (!e.target.files || !e.target.files[0]) return;

    setLoadingImage(true);
    const file = e.target.files[0];
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";

    setPreviewName(file.name);
    setPreviewType(isImage ? "image" : isPdf ? "pdf" : "file");

    if (isImage) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setPreview(reader.result as string);
        setLoadingImage(false);
      });
      reader.readAsDataURL(file);
    } else {
      setPreview(URL.createObjectURL(file));
      setLoadingImage(false);
    }

    props.onNewFile && props.onNewFile(file);
  }

  function handleImageClick() {
    if (!preview) {
      return;
    }

    if (!isImagePreview && !isPdfPreview) {
      window.open(preview, "_blank", "noopener,noreferrer");
      return;
    }

    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
  }

  const handlePopUpOpen = () => {
    if (isReadOnly) return;
    setShowPopup(true);
  };

  const handlePopUpConfirm = () => {
    if (isReadOnly) return;
    setPreview("");
    setPreviewName("");
    props.onRemoveClick && props.onRemoveClick();
    handlePopUpClose();
  };
  const handlePopUpClose = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    if (!props.src) {
      setPreview("");
      setPreviewName("");
      setPreviewType("image");
      setLoadingImage(false);
      return;
    }

    if (!props.src.startsWith("http")) {
      setLoadingImage(false);
      setPreview(buildAssetURL(props.src));
      setPreviewName(props.src.split("/").pop() || props.src);
      setPreviewType(isPdfFile(props.src) ? "pdf" : isImageLikeFile(props.src) ? "image" : "file");
      return;
    }

    setPreview(props.src);
    setPreviewName(props.src.split("/").pop()?.split("?")[0] || props.src);
    setPreviewType(isPdfFile(props.src) ? "pdf" : isImageLikeFile(props.src) ? "image" : "file");
    setLoadingImage(false);
  }, [props.src]);

  return (
    <div
      className={`relative box-file flex justify-center items-center rounded-md ${props.className || ""
        }`}
    >
      {!preview && (
        <label className="h-20 w-24 relative">
          <div className="border-2 border-dashed border-gray2 hover:border-greenPrimary flex flex-col items-center justify-center text-brown h-full">
            <HiCloudUpload className="text-3xl text-yellowPrimary/30 mt-2 absolute top-5 " />
            <span className="text-center">
              enviar <br /> arquivo
            </span>
          </div>
          <input
            alt="enviar arquivo"
            type="file"
            onChange={handleFileInput}
            onClick={(e) => e.stopPropagation()}
            disabled={isReadOnly}
            className="absolute top-0 bottom-0 left-0 right-0 opacity-0 cursor-pointer border p-2 rounded w-full"
          />
        </label>
      )}

      {loadingImage && (
        <div className="preview absolute left-0 right-0 top-0 bottom-0 flex justify-center items-center bg-gray rounded-md">
          <Loading />
        </div>
      )}

      {preview && !loadingImage && (
        <div className="preview absolute left-0 right-0 top-0 bottom-0 flex justify-center items-center bg-gray/50 rounded-md">
          {isImagePreview ? (
            <img src={preview} onClick={handleImageClick} />
          ) : (
            <button
              type="button"
              onClick={handleImageClick}
              className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center text-brown"
            >
              <HiDocumentText className="text-4xl text-yellowPrimary/70" />
              <span className="line-clamp-2 text-sm font-medium">
                {isPdfPreview ? "Visualizar PDF" : "Abrir arquivo"}
              </span>
              <span className="line-clamp-2 break-all text-xs text-black/70">
                {previewName || `arquivo.${getFileExtension(preview) || "bin"}`}
              </span>
            </button>
          )}
        </div>
      )}
      {!props.disableTrashIcon && preview && !loadingImage && (
        <button
          className="absolute top-0 right-0 hover:bg-redPrimary/70 rounded-full bg-black/40 p-2 h-10 w-10 text-white flex justify-center items-center"
          type="button"
          onClick={() => handlePopUpOpen()}
        >
          <HiTrash size={24} />
        </button>
      )}

      {showModal && (
        <ImageModal
          image={preview}
          onClose={handleCloseModal}
          title={previewName}
          fileType={previewType}
        />
      )}
      {showPopup && (
        <PopUpDelete
          disabled={false}
          onConfirm={handlePopUpConfirm}
          onClose={handlePopUpClose}
        />
      )}
    </div>
  );
}
